/**
 * Office.js Body.setSignatureAsync allows max 30,000 characters.
 * Keep a buffer for watermark / prepare wrappers applied at insert time.
 */
export const SET_SIGNATURE_ASYNC_MAX_CHARS = 30_000;
/** Soft save limit — leaves room for wrappers at insert time. */
export const SIGNATURE_SAVE_MAX_CHARS = 28_000;
/** Soft warning while typing (before save is blocked). */
export const SIGNATURE_WARN_CHARS = 25_000;

/**
 * Overhead estimate when combining signature + campaigns for the signature slot:
 * campaign wrappers, separators, classic markers, placeholder expansion.
 * (Pro has campaigns; Free has watermark but no campaigns — buffer covers both cases.)
 */
export const COMBINED_SLOT_OVERHEAD_CHARS = 500;

/** Fixed product defaults (admin UI hidden; not tenant-configurable). */
export const PRODUCT_IMAGE_DEFAULTS = {
  allowImageUrl: true,
  allowImageBase64: false,
  allowImageAsAttachment: true,
  allowedAttachmentFormats: ["png", "jpeg", "gif", "webp"] as const,
  signatureInsertionMode: "officeJs" as const,
};

const CAMPAIGN_BLOCK_SEP = "<br><br>";
const CAMPAIGN_WRAP_PREFIX = '<div class="sunnysign-campaign" data-campaign-id="0">';
const CAMPAIGN_WRAP_SUFFIX = "</div>";

/** Estimated HTML length for setSignatureAsync: signature + selected campaign bodies + overhead. */
export function estimateCombinedSlotLength(
  signatureHtml: string,
  campaignHtmls: string[]
): number {
  const sig = signatureHtml || "";
  const campaigns = campaignHtmls.filter((c) => (c || "").trim().length > 0);
  if (campaigns.length === 0) {
    return sig.length + COMBINED_SLOT_OVERHEAD_CHARS;
  }
  const wrapped = campaigns
    .map((c) => CAMPAIGN_WRAP_PREFIX.length + c.length + CAMPAIGN_WRAP_SUFFIX.length)
    .reduce((a, b) => a + b, 0);
  const seps = CAMPAIGN_BLOCK_SEP.length * campaigns.length; // leading sep after sig + between
  return sig.length + seps + wrapped + COMBINED_SLOT_OVERHEAD_CHARS;
}

export function isOverSaveLimit(len: number): boolean {
  return len > SIGNATURE_SAVE_MAX_CHARS;
}

export function isInWarnBand(len: number): boolean {
  return len >= SIGNATURE_WARN_CHARS && len <= SIGNATURE_SAVE_MAX_CHARS;
}

export function isOverSlotLimit(len: number): boolean {
  return len > SET_SIGNATURE_ASYNC_MAX_CHARS;
}
